/**
 * AdminReliabilityBeta.js
 *
 * Iter-412a — Admin-only Reliability Beta validation console.
 *
 * Purpose: give Mel + ops a single page to spot-check reliability
 * scores against real historical data BEFORE we surface the scores to
 * talent and clients. Ops can flip the admin-only gate on/off from
 * here when they're confident the scoring is right.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';


export default function AdminReliabilityBeta() {
  const [status, setStatus] = useState(null);
  const [validation, setValidation] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, v] = await Promise.all([
        api.get('/reliability/beta/status'),
        api.get('/reliability/beta/validation?limit=50'),
      ]);
      setStatus(s.data);
      setValidation(v.data);
    } catch (e) {
      toast.error('Failed to load beta status');
    }
  };

  useEffect(() => { load(); }, []);

  const toggleGate = async (nextValue) => {
    if (nextValue === true) {
      // No confirm needed to REACTIVATE the admin-only gate.
    } else {
      const ok = window.confirm(
        'Turn OFF admin-only mode and expose reliability scores to talent + clients?\n\n' +
        'Only do this after validating scores against real historical data.'
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await api.post('/reliability/beta/toggle', { admin_only: nextValue });
      toast.success(nextValue ? 'Admin-only mode ENABLED' : 'Score visibility OPENED');
      await load();
    } catch (e) {
      toast.error('Toggle failed');
    } finally {
      setSaving(false);
    }
  };

  if (!status || !validation) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="animate-spin w-6 h-6 text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="reliability-beta-page">
      <Link
        to="/admin/reliability"
        className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        data-testid="back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Reliability
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Reliability Beta — Admin Validation
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Sanity-check reliability scores against real historical data before
            we expose them to talent and clients. Fallback-protected bookings
            (GPS zero-fallback, selfie failures, admin overrides, system errors)
            are never counted as no-shows.
          </p>
        </div>

        <Card className="w-72">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-500 uppercase tracking-wide">
              Gate
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {status.admin_only ? (
                  <ShieldAlert className="w-5 h-5 text-amber-500" />
                ) : (
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                )}
                <span className="font-medium">
                  {status.admin_only ? 'Admin-only' : 'Public'}
                </span>
              </div>
              <Switch
                data-testid="reliability-beta-toggle"
                checked={!status.admin_only}
                disabled={saving}
                onCheckedChange={(open) => toggleGate(!open)}
              />
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {status.admin_only
                ? 'Talent + clients cannot see reliability scores.'
                : 'Scores are visible to talent and clients.'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Protected Statuses & Neutral Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase mb-2">
                Fallback-protected review statuses ({status.fallback_protected_statuses.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {status.fallback_protected_statuses.map((s) => (
                  <Badge key={s} className="bg-slate-100 text-slate-700 border-slate-200" data-testid={`fallback-${s}`}>
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase mb-2">
                Neutral event types ({status.neutral_event_types.length})
              </p>
              <ul className="text-sm space-y-1 text-slate-700">
                {status.neutral_event_types.map((e) => (
                  <li key={e.event_type}>
                    <span className="font-mono text-xs text-slate-500">{e.event_type}</span>
                    <span className="text-slate-400"> — {e.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Top scored talents ({validation.count})
          </CardTitle>
          <Button size="sm" variant="outline" onClick={load} data-testid="refresh-btn">Refresh</Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Talent</th>
                  <th className="text-right px-4 py-2">Score</th>
                  <th className="text-right px-4 py-2">Attendance</th>
                  <th className="text-right px-4 py-2">On-time</th>
                  <th className="text-right px-4 py-2">Completed</th>
                  <th className="text-right px-4 py-2">Assigned</th>
                  <th className="text-right px-4 py-2">Fallback bookings</th>
                  <th className="text-left px-4 py-2">Recent adjustments</th>
                </tr>
              </thead>
              <tbody>
                {validation.talents.map((t) => (
                  <tr key={t.talent_id} className="border-t hover:bg-slate-50" data-testid={`row-${t.talent_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-900">{t.full_name}</div>
                      <div className="text-xs text-slate-500">{t.email}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {t.reliability_score.toFixed(2)}
                      <div className="text-xs text-slate-400">{t.badge_level}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{t.attendance_rate.toFixed(0)}%</td>
                    <td className="px-4 py-2 text-right">{t.on_time_rate.toFixed(0)}%</td>
                    <td className="px-4 py-2 text-right">{t.total_shifts_completed}</td>
                    <td className="px-4 py-2 text-right">{t.total_shifts_assigned}</td>
                    <td className="px-4 py-2 text-right">
                      {t.fallback_protected_bookings > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                          {t.fallback_protected_bookings}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {t.recent_adjustments.length === 0 ? (
                        <span className="text-slate-400 text-xs">none</span>
                      ) : (
                        <ul className="text-xs space-y-0.5">
                          {t.recent_adjustments.slice(0, 3).map((a, i) => (
                            <li key={i} className="text-slate-600">
                              <span className={a.delta < 0 ? 'text-red-600' : a.delta > 0 ? 'text-emerald-600' : 'text-slate-400'}>
                                {a.delta > 0 ? '+' : ''}{a.delta}
                              </span>{' '}
                              {a.event_type}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
